/**
 * 项目卡片的「真实进度」推导。
 *
 * ## 为什么不用 status 字段（2026-10-07 实测）
 *
 * 库里 17 个项目的 `status` **全部是 `draft`**，`final_video_url` 全部为空。
 * 也就是说卡片上那枚「草稿 / 处理中 / 已完成」徽章在当前数据下 100% 显示同一句话，
 * 17 张卡在视觉上完全无法区分 —— 用户只能靠标题认项目，得靠点进去才知道做到哪了。
 *
 * `status` 也不是没用：它是「整体在不在跑批」的粗粒度标记，会在批处理时被改写。
 * 但把它当成卡片唯一的身份标识，就是拿一个常量当信息。所以：
 *
 *   · `status` 降级为**辅助信号**：只有 `processing` 时才额外提示（那一档本来就醒目）。
 *   · 卡片的身份标识改为**从内容推导的阶段**（下面 `deriveProjectStage`）。
 *   · 有 `finalVideoUrl` 时无条件盖上「已成片」——成片是用户唯一真正关心的终态。
 *
 * ## 阶段顺序即优先级
 *
 *   empty（未开坑） < outline（已立项） < scripting（剧本/分集）
 *   < storyboard（分镜就绪） < producing（出片中） < done（已成片）
 *
 * 推导只用**库内实际产物**，不用配置意图：一个项目填了目标集数但一集没拆，
 * 仍然是 `outline` 而不是「进度 0%」。这样卡片上的每个数字都能被点进去验证。
 */

export type ProjectStage =
  | "empty"
  | "outline"
  | "scripting"
  | "storyboard"
  | "producing"
  | "done";

/** 阶段从低到高，用于「进行中」筛选与排序（数字大 = 更靠后 = 更接近成片）。 */
const STAGE_ORDER: Record<ProjectStage, number> = {
  empty: 0,
  outline: 1,
  scripting: 2,
  storyboard: 3,
  producing: 4,
  done: 5,
};

export interface ProjectStatsInput {
  /** 有分集 */
  episodeCount: number;
  /** 分镜总数 */
  shotCount: number;
  /** 已有成片镜头的分镜数 */
  videoShotCount: number;
  /** 项目级成片 */
  finalVideoUrl?: string | null;
  /** 库里的 status 原值（辅助信号，非唯一判据） */
  status?: string | null;
}

export interface ProjectStats {
  stage: ProjectStage;
  /** 出片进度 0~1；无分镜时为 0（而不是 NaN，避免进度条渲染成空白） */
  videoProgress: number;
  /** 制作中的镜头数（已出片 < 总数 > 0），用于「还剩几镜」 */
  remainingShots: number;
}

/**
 * 从库内产物推导阶段。
 *
 * 判据顺序有讲究，**先看有没有成片再看中间态** —— 一个已出成片的项目
 * 不该因为「还有几镜没出」被降级回 `producing`，成片是不可逆的终态。
 */
export function deriveProjectStage(input: ProjectStatsInput): ProjectStats {
  const { episodeCount, shotCount, videoShotCount } = input;

  // 成片是终态，优先级最高（哪怕还挂着 draft —— 真实库里就这么存过）
  if (input.finalVideoUrl && input.finalVideoUrl.trim() !== "") {
    return { stage: "done", videoProgress: 1, remainingShots: 0 };
  }

  const videoProgress = shotCount > 0 ? Math.min(1, videoShotCount / shotCount) : 0;
  const remainingShots = Math.max(0, shotCount - videoShotCount);

  if (episodeCount === 0) {
    // 没分集：区分「连剧本都没写」与「剧本写了但没拆分集」——
    // 前者卡片上给「去写剧本」的直达入口，后者给「去拆分集」。
    return {
      stage: shotCount > 0 ? "scripting" : "empty",
      videoProgress,
      remainingShots,
    };
  }
  if (shotCount === 0) {
    return { stage: "outline", videoProgress: 0, remainingShots: 0 };
  }
  if (videoShotCount > 0) {
    return { stage: "producing", videoProgress, remainingShots };
  }
  return { stage: "storyboard", videoProgress: 0, remainingShots };
}

/** 排序用：阶段序号（越大越接近成片）。 */
export function stageRank(stage: ProjectStage): number {
  return STAGE_ORDER[stage];
}

/**
 * 相对时间的**结构化**结果（不给成品字符串）。
 *
 * 为什么不直接返回 "3 天前"：
 *   i18n 门禁（scripts/i18n-keys.test.mjs）要求 zh/en 两个 locale 的键集合完全相等，
 *   而"刚刚 / N 分钟前 / N 小时前 / N 天前"是四个不同句子 × 四语言 = 16 条文案。
 *   把它们塞进一个函数里硬编码，等于绕开 i18n 体系在组件里造第二套文案来源 ——
 *   这正是本项目此前 `MISSING_MESSAGE` 类 bug 的成因。所以这里只出「是多久」，
 *   句子交给 `t()` 渲染。
 *
 * 分档阈值按「列表刷新频率」定：
 *   <1 分钟 → justNow；<1 小时 → minutes；<24 小时 → hours；<30 天 → days；
 *   更久 → date（具体日期，再往前就不再逐日变化了，显示天数只是噪声）。
 */
export type RelativeTime =
  | { kind: "justNow" }
  | { kind: "minutes"; value: number }
  | { kind: "hours"; value: number }
  | { kind: "days"; value: number }
  | { kind: "date"; value: Date };

export function relativeTimeOf(from: Date, now: Date = new Date()): RelativeTime {
  const diffSec = Math.round((now.getTime() - from.getTime()) / 1000);
  if (diffSec < 60) return { kind: "justNow" };
  const min = Math.floor(diffSec / 60);
  if (min < 60) return { kind: "minutes", value: min };
  const hours = Math.floor(min / 60);
  if (hours < 24) return { kind: "hours", value: hours };
  const days = Math.floor(hours / 24);
  if (days < 30) return { kind: "days", value: days };
  return { kind: "date", value: from };
}

/** 大数字缩写：1234 → "1.2k"。给统计 chip 用，避免 1098 把卡片撑破。 */
export function formatCount(n: number): string {
  if (n < 1000) return String(n);
  return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
}
